import { Link, useNavigate } from 'react-router';
import {
  Button,
  Card,
  EmptyState,
  Status,
  presentation,
  VERIFICATION_STATES,
} from '@gatecheckpro/design-system';
import { useSession, useViewer } from '../lib/session.tsx';

/**
 * The organization picker.
 *
 * The one place a person chooses which tenant they are acting in. Every route
 * below it carries the organization in the path, so "which organization am I
 * in" is never ambient state that a stale tab can get wrong.
 */
export function OrganizationsPage() {
  const viewer = useViewer();
  const { signOut } = useSession();
  const navigate = useNavigate();

  return (
    <div className="gc-app">
      <header className="gc-header">
        <div className="gc-container gc-header__inner">
          <span className="gc-wordmark">
            GateCheck<span className="gc-wordmark__accent">Pro</span>
            <span className="gc-wordmark__suffix">Organizer</span>
          </span>
          <div className="gc-row" style={{ marginInlineStart: 'auto' }}>
            <Link to="/security" className="gc-nav__link">
              Security
            </Link>
            <button className="gc-button gc-button--quiet" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose">
          <div className="gc-page-header">
            <span className="gc-eyebrow">Signed in as {viewer.displayName}</span>
            <h1>Your organizations</h1>
            <p className="gc-lede">
              An organization is the boundary for everything that follows — events, gates, ticket
              products, orders and settlement all belong to exactly one.
            </p>
          </div>

          {viewer.organizations.length === 0 ? (
            <EmptyState
              title="You are not in an organization yet"
              action={
                <Button variant="primary" onClick={() => void navigate('/organizations/new')}>
                  Create an organization
                </Button>
              }
            >
              Create one to become its owner, or accept an invitation someone has sent you.
              <div style={{ marginTop: '0.75rem' }}>
                <Link to="/invitations/accept">I have an invitation code</Link>
              </div>
            </EmptyState>
          ) : (
            <>
              <div className="gc-grid">
                {viewer.organizations.map((organization) => {
                  const verification = presentation(
                    VERIFICATION_STATES,
                    organization.verificationState,
                  );
                  return (
                    <Card key={organization.organizationId}>
                      <div className="gc-stack">
                        <div className="gc-stack gc-stack--tight">
                          <h2>{organization.name}</h2>
                          <span className="gc-muted">{organization.slug}</span>
                        </div>
                        <div className="gc-row">
                          <Status
                            tone={verification.tone}
                            glyph={verification.glyph}
                            label={verification.label}
                            title={verification.meaning}
                          />
                        </div>
                        <p className="gc-muted">
                          {organization.roles.length === 0
                            ? 'No roles granted'
                            : organization.roles.join(' · ')}
                        </p>
                        <Button
                          variant="primary"
                          onClick={() => void navigate(`/o/${organization.organizationId}`)}
                        >
                          Open workspace
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>

              <div className="gc-row">
                <Button onClick={() => void navigate('/organizations/new')}>
                  Create another organization
                </Button>
                <Link to="/invitations/accept" className="gc-nav__link">
                  Accept an invitation
                </Link>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
